import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import {
  announceListItemMoved,
  announceListItemRemoved,
  moveFormArrayItem,
  reorderFormArrayItem,
} from '../../../functions/list-reorder';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { ReportFieldTileComponent } from './report-field-tile.component';

/** Display model for one tile/row in {@link ReorderableFieldListComponent}. */
export interface ReorderableFieldItem {
  id: string;
  /** Tile text (selected label or empty/slot label). */
  label: string;
  hasField: boolean;
  disabled?: boolean;
  /** Show edit (and clear when showClearOnTile). Default true. */
  updateable?: boolean;
  showLockIcon?: boolean;
  dragDisabled?: boolean;
  /** Show move chevrons for this item. */
  showMove?: boolean;
  disableEarlier?: boolean;
  disableLater?: boolean;
  /** Row-layout remove X (independent of tile clear). */
  showRemove?: boolean;
  removeAriaLabel?: string;
  fixedWidth?: boolean;
  wrapLabel?: boolean;
  minHeight?: string;
  cursorOnTile?: boolean;
}

export interface ReorderableFieldItemContext {
  $implicit: ReorderableFieldItem;
  index: number;
}

/** Parent-supplied row chrome when using FormArray mode; list fills edge flags. */
export interface ReorderableFormRowDescriptor
  extends Omit<ReorderableFieldItem, 'disableEarlier' | 'disableLater'> {
  disableEarlier?: boolean;
  disableLater?: boolean;
}

export interface ReorderableFormRowContext {
  $implicit: FormGroup;
  index: number;
  item: ReorderableFieldItem;
}

export interface ReorderableFormChangedEvent {
  reason: 'move' | 'drop' | 'remove';
  id: string;
  previousIndex: number;
  currentIndex: number;
  label: string;
}

export type ReorderableFieldListLayout = 'grid' | 'row';

/**
 * Shared reorder list (grid ←→ or row ↑↓).
 *
 * Presentational: pass `[items]` (+ optional `itemExtras`); parent owns move/drop/remove.
 * FormArray mode: pass `[formArray]`, `focusHost`, `[mapRow]`, optional `rowExtras`;
 * this component owns move / drop / remove, focus restore, and announcements, and
 * emits `changed` for parent persist.
 */
@Component({
  selector: 'app-reorderable-field-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
    IconButtonComponent,
    ReportFieldTileComponent,
  ],
  template: `
    <ng-template
      #moveButtons
      let-item
      let-i="index"
      let-orientation="orientation"
    >
      <div
        class="field-move-actions tw-flex"
        [class.tw-flex-col]="orientation === 'vertical'"
        [class.tw-justify-center]="orientation === 'horizontal'"
        [class.tw-px-1]="orientation === 'vertical'"
      >
        <app-icon-button
          [icon]="
            orientation === 'vertical'
              ? 'fa-light fa-chevron-up'
              : 'fa-light fa-chevron-left'
          "
          [ariaLabel]="
            'Move ' +
            item.label +
            ' ' +
            (orientation === 'vertical' ? 'up' : 'earlier')
          "
          [disabled]="!!item.disableEarlier"
          (pointerdown)="$event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          (click)="onMoveEmit(item, i, -1, $event)"
        />
        <app-icon-button
          [icon]="
            orientation === 'vertical'
              ? 'fa-light fa-chevron-down'
              : 'fa-light fa-chevron-right'
          "
          [ariaLabel]="
            'Move ' +
            item.label +
            ' ' +
            (orientation === 'vertical' ? 'down' : 'later')
          "
          [disabled]="!!item.disableLater"
          (pointerdown)="$event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          (click)="onMoveEmit(item, i, 1, $event)"
        />
      </div>
    </ng-template>

    @if (layout() === 'grid') {
      <div
        class="reorder-grid tw-grid tw-grid-cols-1 tw-items-stretch sm:tw-grid-cols-2 tw-gap-x-2 tw-gap-y-4"
        [class.md:tw-grid-cols-8]="columns() === 8"
        [style.grid-template-columns]="gridTemplateColumns()"
        cdkDropList
        cdkDropListOrientation="mixed"
        [cdkDropListData]="resolvedDropListData()"
        (cdkDropListDropped)="onDropListDropped($event)"
      >
        @for (item of displayItems(); track item.id; let i = $index) {
          <div
            class="reorder-cell tw-flex tw-h-full tw-min-h-0 tw-flex-col"
            [attr.data-move-id]="item.id"
            cdkDrag
            [cdkDragDisabled]="!!item.dragDisabled"
            tabindex="-1"
          >
            <div
              class="tw-min-h-0 tw-flex-1 tw-cursor-grab active:tw-cursor-grabbing"
              cdkDragHandle
              [class.tw-cursor-not-allowed]="item.disabled"
              [class.tw-cursor-grab]="!item.disabled"
            >
              <app-report-field-tile
                class="tw-h-full"
                [label]="item.label"
                [hasField]="item.hasField"
                [disabled]="!!item.disabled"
                [showEditButton]="item.updateable !== false"
                [showClearButton]="
                  showClearOnTile() && item.updateable !== false
                "
                [showLockIcon]="!!item.showLockIcon"
                [fixedWidth]="item.fixedWidth !== false"
                [wrapLabel]="item.wrapLabel !== false"
                [cursorOnTile]="!!item.cursorOnTile"
                [minHeight]="item.minHeight || '40px'"
                (edit)="onEditEmit(item, i)"
                (clear)="onClearEmit(item, i)"
              />
            </div>
            @if (item.showMove) {
              <ng-container
                *ngTemplateOutlet="
                  moveButtons;
                  context: { $implicit: item, index: i, orientation: 'horizontal' }
                "
              />
            }
          </div>
        }
      </div>
    } @else {
      <div
        class="reorder-rows"
        cdkDropList
        cdkDropListOrientation="vertical"
        [cdkDropListData]="resolvedDropListData()"
        (cdkDropListDropped)="onDropListDropped($event)"
      >
        @for (item of displayItems(); track item.id; let i = $index; let last = $last) {
          <div
            class="reorder-row tw-flex tw-items-start sm:tw-items-center tw-gap-2 tw-py-3"
            [class.tw-bg-[var(--sed-warm-white-color)]]="rowHighlight()"
            [class.tw-rounded-lg]="rowHighlight()"
            [attr.data-move-id]="item.id"
            [class.tw-pb-0]="!last"
            cdkDrag
            [cdkDragDisabled]="!!item.dragDisabled"
          >
            <div
              class="tw-bg-[var(--sed-grey-60-color)] tw-rounded-lg tw-min-h-[50px] tw-border-[var(--sed-black-80-color)] tw-border-2 tw-border-dotted tw-animate-pulse"
              *cdkDragPlaceholder
            ></div>

            @if (item.showMove) {
              <ng-container
                *ngTemplateOutlet="
                  moveButtons;
                  context: { $implicit: item, index: i, orientation: 'vertical' }
                "
              />
            }

            <div
              class="tw-cursor-grab active:tw-cursor-grabbing"
              [class.tw-cursor-not-allowed]="item.dragDisabled || item.disabled"
              [class.tw-cursor-grab]="!item.dragDisabled && !item.disabled"
              cdkDragHandle
            >
              <app-report-field-tile
                [label]="item.label"
                [hasField]="item.hasField"
                [disabled]="!!item.disabled"
                [showEditButton]="item.updateable !== false"
                [showClearButton]="
                  showClearOnTile() && item.updateable !== false
                "
                [showLockIcon]="!!item.showLockIcon"
                [fixedWidth]="item.fixedWidth !== false"
                [wrapLabel]="item.wrapLabel !== false"
                [cursorOnTile]="!!item.cursorOnTile"
                [minHeight]="item.minHeight || '40px'"
                (edit)="onEditEmit(item, i)"
                (clear)="onClearEmit(item, i)"
              />
            </div>

            @if (rowExtras()) {
              <ng-container
                *ngTemplateOutlet="
                  rowExtras()!;
                  context: { $implicit: groupAt(i), index: i, item }
                "
              />
            } @else if (itemExtras()) {
              <ng-container
                *ngTemplateOutlet="
                  itemExtras()!;
                  context: { $implicit: item, index: i }
                "
              />
            }

            @if (item.showRemove) {
              <div>
                <app-icon-button
                  icon="fa-light fa-xmark"
                  (click)="onRemoveEmit(item, i, $event)"
                  [ariaLabel]="item.removeAriaLabel || removeAriaLabel()"
                />
              </div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: `
    .reorder-cell.cdk-drag-placeholder {
      opacity: 0.45;
      outline: 2px dashed var(--sed-black-80-color);
      outline-offset: -2px;
      border-radius: 0.5rem;
      background: transparent !important;
      box-shadow: none !important;
    }

    .reorder-cell.cdk-drag-preview {
      height: auto !important;
      min-height: 0 !important;
    }

    .reorder-cell.cdk-drag-animating {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }

    .cdk-drop-list-dragging .reorder-cell:not(.cdk-drag-placeholder) {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }

    /* Hide chevrons in the drag ghost. */
    .reorder-cell.cdk-drag-preview .field-move-actions,
    .reorder-row.cdk-drag-preview .field-move-actions {
      display: none !important;
    }
  `,
})
export class ReorderableFieldListComponent {
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Invalidates FormArray-derived items when values change without length change. */
  private readonly rowVersion = signal(0);

  layout = input<ReorderableFieldListLayout>('row');

  /**
   * Grid only. `8` keeps the existing md:8-col report-fields layout.
   * Omit / null → CSS auto-fill (tiles take available space and wrap).
   */
  columns = input<number | null>(8);

  /** Presentational mode: parent-built display items. Ignored when `formArray` is set. */
  items = input<ReorderableFieldItem[]>([]);

  /**
   * FormArray mode: list owns reorder/remove/announce.
   * Requires `focusHost` + `mapRow`.
   */
  formArray = input<FormArray | null>(null);

  /**
   * Host selector for focus restore in FormArray mode, e.g. `app-sort-options`.
   * Rows expose `[attr.data-move-id]`.
   */
  focusHost = input<string | null>(null);

  /** Control name that holds the stable row id (FormArray mode). */
  idKey = input('id');

  /** When true, move chevrons and drag stay off (e.g. SSPB lock order). */
  reorderLocked = input(false);

  /**
   * Maps each FormGroup to tile/row chrome (FormArray mode).
   * List adds first/last disable flags and default `showMove`.
   */
  mapRow = input<
    | ((
        group: FormGroup,
        index: number,
        length: number,
      ) => ReorderableFormRowDescriptor)
    | null
  >(null);

  /** CDK drop list data; defaults to FormArray controls or `items()`. */
  dropListData = input<unknown>(undefined);

  /** Tile top-right clear X (keep slot). Independent of row remove. */
  showClearOnTile = input(true);

  /** Warm background on row items (sort). */
  rowHighlight = input(false);

  removeAriaLabel = input('Remove item');

  /** Presentational trailing controls (`$implicit` = item). */
  itemExtras = input<TemplateRef<ReorderableFieldItemContext> | null>(null);

  /** FormArray trailing controls (`$implicit` = FormGroup). */
  rowExtras = input<TemplateRef<ReorderableFormRowContext> | null>(null);

  edit = output<{ item: ReorderableFieldItem; index: number }>();
  clear = output<{ item: ReorderableFieldItem; index: number }>();
  remove = output<{ item: ReorderableFieldItem; index: number }>();
  move = output<{
    item: ReorderableFieldItem;
    index: number;
    delta: -1 | 1;
    event: Event;
  }>();
  dropped = output<CdkDragDrop<unknown>>();
  /** FormArray mode: after a successful structural change (parent persist). */
  changed = output<ReorderableFormChangedEvent>();

  constructor() {
    effect((onCleanup) => {
      const fa = this.formArray();
      if (!fa) {
        return;
      }
      const bump = (): void => {
        this.rowVersion.update((v) => v + 1);
        this.cdr.markForCheck();
      };
      const sub = fa.valueChanges.subscribe(() => bump());
      onCleanup(() => sub.unsubscribe());
    });
  }

  private readonly isFormMode = computed(
    () => !!this.formArray() && !!this.mapRow() && !!this.focusHost(),
  );

  displayItems = computed((): ReorderableFieldItem[] => {
    if (!this.isFormMode()) {
      return this.items();
    }
    // Must read rowVersion itself — a length-only tick is Object.is-equal after
    // reorder, so Angular would keep a stale computed list (visual freeze).
    this.rowVersion();
    const fa = this.formArray()!;
    const n = fa.length;
    const locked = this.reorderLocked();
    const mapRow = this.mapRow()!;
    return fa.controls.map((ctrl, i) => {
      const base = mapRow(ctrl as FormGroup, i, n);
      return {
        ...base,
        showMove: locked ? false : (base.showMove ?? n >= 2),
        disableEarlier: base.disableEarlier ?? i === 0,
        disableLater: base.disableLater ?? i === n - 1,
        dragDisabled: locked || !!base.dragDisabled,
      };
    });
  });

  resolvedDropListData = computed((): unknown => {
    const explicit = this.dropListData();
    if (explicit !== undefined) {
      return explicit;
    }
    const fa = this.formArray();
    if (fa) {
      return fa.controls;
    }
    return this.displayItems();
  });

  groupAt(index: number): FormGroup | null {
    const fa = this.formArray();
    if (!fa) {
      return null;
    }
    return fa.at(index) as FormGroup;
  }

  private rowId(ctrl: AbstractControl): string {
    return String(ctrl.get(this.idKey())?.value ?? '');
  }

  private rowLabel(index: number): string {
    return this.displayItems()[index]?.label || 'Item';
  }

  onEditEmit(item: ReorderableFieldItem, index: number): void {
    this.edit.emit({ item, index });
  }

  onClearEmit(item: ReorderableFieldItem, index: number): void {
    this.clear.emit({ item, index });
  }

  onMoveEmit(
    item: ReorderableFieldItem,
    index: number,
    delta: -1 | 1,
    event: Event,
  ): void {
    if (!this.isFormMode()) {
      this.move.emit({ item, index, delta, event });
      return;
    }
    if (this.reorderLocked()) {
      return;
    }
    event.stopPropagation();
    const fa = this.formArray()!;
    const resolved = fa.controls.findIndex((c) => this.rowId(c) === item.id);
    if (resolved < 0) {
      return;
    }
    const label = this.rowLabel(resolved);
    const host = this.focusHost()!;
    const idKey = this.idKey();
    const moved = moveFormArrayItem({
      formArray: fa,
      index: resolved,
      delta,
      activator: event,
      focusContainerSelector: (ctrl) => {
        const id = ctrl.get(idKey)?.value as string | undefined;
        return id ? `${host} [data-move-id="${CSS.escape(id)}"]` : null;
      },
      announce: {
        announcer: this.liveAnnouncer,
        label,
        orientation: this.layout() === 'grid' ? 'horizontal' : 'vertical',
      },
    });
    if (!moved) {
      return;
    }
    // FormArray mutation is not a signal dep — force displayItems() to remap order.
    this.rowVersion.update((v) => v + 1);
    this.cdr.markForCheck();
    this.changed.emit({
      reason: 'move',
      id: item.id,
      previousIndex: resolved,
      currentIndex: resolved + delta,
      label,
    });
  }

  onDropListDropped(event: CdkDragDrop<unknown>): void {
    if (!this.isFormMode()) {
      this.dropped.emit(event);
      return;
    }
    if (this.reorderLocked()) {
      return;
    }
    const fa = this.formArray()!;
    const label = this.rowLabel(event.previousIndex);
    const id = this.rowId(fa.at(event.previousIndex));
    if (!reorderFormArrayItem(fa, event.previousIndex, event.currentIndex)) {
      return;
    }
    announceListItemMoved({
      announcer: this.liveAnnouncer,
      label,
      newIndex: event.currentIndex,
      total: fa.length,
      delta: event.currentIndex > event.previousIndex ? 1 : -1,
      orientation: this.layout() === 'grid' ? 'horizontal' : 'vertical',
    });
    this.rowVersion.update((v) => v + 1);
    this.cdr.markForCheck();
    this.changed.emit({
      reason: 'drop',
      id,
      previousIndex: event.previousIndex,
      currentIndex: event.currentIndex,
      label,
    });
  }

  onRemoveEmit(
    item: ReorderableFieldItem,
    index: number,
    event: Event,
  ): void {
    event.stopPropagation();
    if (!this.isFormMode()) {
      this.remove.emit({ item, index });
      return;
    }
    const fa = this.formArray()!;
    const resolved = fa.controls.findIndex((c) => this.rowId(c) === item.id);
    if (resolved < 0) {
      return;
    }
    const label = item.label || this.rowLabel(resolved);
    const id = item.id;
    fa.removeAt(resolved);
    announceListItemRemoved({ announcer: this.liveAnnouncer, label });
    this.rowVersion.update((v) => v + 1);
    this.cdr.markForCheck();
    this.changed.emit({
      reason: 'remove',
      id,
      previousIndex: resolved,
      currentIndex: resolved,
      label,
    });
  }

  gridTemplateColumns(): string | null {
    if (this.layout() !== 'grid') {
      return null;
    }
    const cols = this.columns();
    if (cols == null) {
      return 'repeat(auto-fill, minmax(120px, 1fr))';
    }
    // Prefer Tailwind md:tw-grid-cols-8 when columns === 8; leave inline unset.
    if (cols === 8) {
      return null;
    }
    return `repeat(${cols}, minmax(0, 1fr))`;
  }
}
